/**
 * 加载指示器组件
 *
 * 用于显示加载状态的组件
 */
import * as React from 'react'
import { cn } from '@/lib/utils'

/* ============================================
 * 类型定义
 * ============================================ */

/** 加载指示器属性 */
interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 尺寸 */
  size?: 'sm' | 'md' | 'lg'
}

/* ============================================
 * 组件实现
 * ============================================ */

/**
 * 加载指示器组件
 *
 * @example
 * // 默认大小
 * <Spinner />
 *
 * @example
 * // 小尺寸
 * <Spinner size="sm" />
 *
 * @example
 * // 大尺寸
 * <Spinner size="lg" />
 */
const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size = 'md', ...props }, ref) => {
    // 尺寸映射
    const sizeClasses = {
      sm: 'h-4 w-4 border-2',
      md: 'h-8 w-8 border-2',
      lg: 'h-12 w-12 border-3',
    }

    return (
      <div
        ref={ref}
        className={cn(
          'animate-spin rounded-full border-primary border-t-transparent',
          sizeClasses[size],
          className
        )}
        {...props}
      />
    )
  }
)

Spinner.displayName = 'Spinner'

/* ============================================
 * 加载容器组件
 * ============================================ */

/** 加载容器属性 */
interface LoadingContainerProps {
  /** 加载状态 */
  loading: boolean
  /** 子组件 */
  children: React.ReactNode
  /** 加载文本 */
  text?: string
  /** 容器类名 */
  className?: string
}

/**
 * 加载容器组件
 *
 * @example
 * <LoadingContainer loading={isLoading}>
 *   <YourContent />
 * </LoadingContainer>
 */
function LoadingContainer({ loading, children, text = '加载中...', className }: LoadingContainerProps) {
  return (
    <div className={cn('relative', className)}>
      {/* 加载遮罩 */}
      {loading && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80">
          <Spinner size="lg" />
          <p className="mt-2 text-sm text-neutral-500">{text}</p>
        </div>
      )}
      {/* 内容 */}
      {children}
    </div>
  )
}

/* ============================================
 * 导出
 * ============================================ */

export { Spinner, LoadingContainer }
export default Spinner